import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import styles from './SyncConflicts.module.scss';

/** Fichier modifié dans le panel ET sur GitHub (pack : develop, container : main). */
export interface SyncConflictItem {
  path: string;
  /** Sauvegarde serveur qui garde la version du panel d'avant l'intégration. */
  backup?: string;
  /** Clés modifiées des deux côtés (JSON de container fusionné), vide pour un fichier entier. */
  keys?: string[];
  /** Raison d'un conflit de fichier entier (fichier non JSON, suppression, fusion impossible). */
  note?: string;
}

/**
 * Conflits d'intégration à trancher, communs aux packs et aux containers : « Garder la version du
 * panel » (elle sera publiée telle quelle) ou « Prendre celle de GitHub ». Rien n'est perdu : la
 * version du panel reste dans la sauvegarde du serveur.
 */
export function ConflictList({
  conflicts, remoteLabel, busyPath, disabled, onKeepPanel, onTakeRemote, takeTitle,
}: {
  conflicts: SyncConflictItem[];
  /** « GitHub » pour un pack, « main » pour un container. */
  remoteLabel: string;
  busyPath: string | null;
  disabled: boolean;
  onKeepPanel: (c: SyncConflictItem) => void;
  onTakeRemote: (c: SyncConflictItem) => void;
  /** Bulle du bouton « Prendre celle de GitHub ». */
  takeTitle?: (c: SyncConflictItem) => string | undefined;
}) {
  if (conflicts.length === 0) return null;
  const n = conflicts.length;
  return (
    <div className={styles.conflicts}>
      <div className={styles.conflictsTitle}>
        <AlertTriangle size={13} />
        {n} conflit{n > 1 ? 's' : ''} : modifié ici ET sur {remoteLabel}.
        La version du panel est gardée, sa publication est bloquée tant que ce n'est pas tranché.
      </div>
      {conflicts.map((c) => {
        const busy = busyPath === c.path;
        const keys = c.keys ?? [];
        return (
          <div key={c.path} className={styles.conflictRow} title={c.backup ? `Sauvegarde : ${c.backup}` : undefined}>
            <span className={styles.conflictPath}>
              <span className={styles.filePath}>{c.path}</span>
              {keys.length > 0 ? (
                <span className={styles.conflictDetail}>
                  clé{keys.length > 1 ? 's' : ''} en conflit : {keys.join(', ')}
                </span>
              ) : c.note ? (
                <span className={styles.conflictDetail}>{c.note}</span>
              ) : null}
            </span>
            <button className={styles.conflictBtn} disabled={disabled} onClick={() => onKeepPanel(c)}>
              {busy ? <Loader2 size={12} className={styles.spin} /> : null} Garder la version du panel
            </button>
            <button className={styles.conflictBtn} disabled={disabled} onClick={() => onTakeRemote(c)}
              title={takeTitle?.(c)}>
              Prendre celle de GitHub
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Ligne de synchronisation à mettre en avant : conflit, avertissement ou échec. */
export function isSyncWarning(m: string): boolean {
  return /^(CONFLIT|Attention)/.test(m) || /en échec|non publié|erreur|refusée/i.test(m);
}

/** Messages d'une synchronisation, conflits et avertissements en évidence. */
export function SyncMessages({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <ul className={styles.syncMessages}>
      {messages.map((m, i) => {
        const warn = isSyncWarning(m);
        return (
          <li key={i} className={warn ? styles.syncWarn : undefined}>
            {warn ? <AlertTriangle size={12} /> : <Check size={12} />} {m}
          </li>
        );
      })}
    </ul>
  );
}
